// Modular translations system - imports from clean modules
import { formatCount as formatCountUtil } from '../utils/numberFormatter';

import { about } from './modules/about';
import { achievementContent } from './modules/achievementContent';
import { admin } from './modules/admin';
import { auth } from './modules/auth';
import { bibleBooks } from './modules/bibleBooks';
import { bibleVerses } from './modules/bibleVerses';
import { bookThemes } from './modules/bookThemes';
import { certificates } from './modules/certificates';
import { coreUI } from './modules/coreUI';
import { familiesFriends } from './modules/familiesFriends';
import { gamification } from './modules/gamification';
import { guestMode } from './modules/guestMode';
import { landing } from './modules/landing';
import { monetization } from './modules/monetization';
import { leaderboard } from './modules/leaderboard';
import { moreScreen } from './modules/moreScreen';
import { multiplayerLobby } from './modules/multiplayerLobby';
import { multiplayerWaiting } from './modules/multiplayerWaiting';
import { onboarding } from './modules/onboarding';
import { profile } from './modules/profile';
import { premiumGateCopy } from './modules/premiumGateCopy';
import { quickActions } from './modules/quickActions';
import { quizFlow } from './modules/quizFlow';
import { results } from './modules/results';
import { settings } from './modules/settings';
import { uiCopy } from './modules/uiCopy';
import { whatsappEngagement } from './modules/whatsappEngagement';


// Type definitions for translation objects
interface TranslationModule {
  [language: string]: Record<string, unknown>;
}

// Helper function to merge translation objects
const mergeTranslations = (language: string) => {
  return {
    // Core UI elements (navigation, testament screens, difficulty, etc.)
    coreUI: (coreUI as TranslationModule)[language] || {},
    ...(coreUI as TranslationModule)[language] || {},
    
    // Bible books translations
    bible_books: (bibleBooks as TranslationModule)[language] || {},
    
    // Book themes and metadata
    ...(bookThemes as TranslationModule)[language] || {},
    
    // Feature-specific modules
    familiesFriends: (familiesFriends as TranslationModule)[language] || {},
    multiplayer_lobby: (multiplayerLobby as TranslationModule)[language] || {},
    multiplayerLobby: (multiplayerLobby as TranslationModule)[language] || {},
    multiplayer_waiting: (multiplayerWaiting as TranslationModule)[language] || {},
    leaderboard: (leaderboard as TranslationModule)[language] || {},
    profile: (profile as TranslationModule)[language] || {},
    settings: (settings as TranslationModule)[language] || {},
    quiz_flow: (quizFlow as TranslationModule)[language] || {},
    quizFlow: (quizFlow as TranslationModule)[language] || {},
    gamification: (gamification as TranslationModule)[language] || {},
    results: (results as TranslationModule)[language] || {},
    certificates: (certificates as TranslationModule)[language] || {},
    moreScreen: (moreScreen as TranslationModule)[language] || {},
    quickActions: (quickActions as TranslationModule)[language] || {},
    admin: (admin as TranslationModule)[language] || {},
    about: (about as TranslationModule)[language] || {},
    achievementContent: (achievementContent as TranslationModule)[language] || {},
    uiCopy: (uiCopy as TranslationModule)[language] || {},
    premiumGateCopy: (premiumGateCopy as TranslationModule)[language] || {},
    onboarding: (onboarding as TranslationModule)[language] || {},
    auth: (auth as TranslationModule)[language] || {},
    bibleVerses: (bibleVerses as TranslationModule)[language] || {},
    whatsappEngagement: (whatsappEngagement as TranslationModule)[language] || {},
    landing: (landing as TranslationModule)[language] || {},
    monetization: (monetization as TranslationModule)[language] || {},
    guestMode: (guestMode as TranslationModule)[language] || {},
  };
};

// Supported languages
const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'de', 'pt', 'ru', 'tl', 'it', 'am', 'ko'];

// Build the translations object dynamically
export const translations: Record<string, Record<string, unknown>> = {};

SUPPORTED_LANGUAGES.forEach(lang => {
  translations[lang] = mergeTranslations(lang);
});

// Translation function with fallback support
export const t = (key: string, language: string = 'en'): string => {
  const keys = key.split('.');
  let current = translations[language];
  
  // Navigate through nested keys
  for (const k of keys) {
    if (current && typeof current === 'object' && k in current) {
      current = current[k];
    } else {
      // Fallback to English if key not found in current language
      if (language !== 'en') {
        return t(key, 'en');
      }
      // If key not found in English either, return the key itself
      return key;
    }
  }
  
  return typeof current === 'string' ? current : key;
};

// Number formatting function with internationalization support
export const formatNumber = (num: number, language: string = 'en'): string => {
  return formatCountUtil(num, language);
};

// Export default for backward compatibility
export default { translations, t };
